// src/components/Sidebar.js
import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAccess } from '../contexts/AccessContext';
import { ROLE_LABELS } from '../config/roles';

const styles = {
  sidebar: {
    width: 'var(--sidebar-width)',
    minHeight: '100vh',
    background: 'var(--ka-navy)',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    position: 'fixed',
    top: 0,
    left: 0,
    bottom: 0,
    zIndex: 100,
    overflowY: 'auto',
    boxShadow: '3px 0 20px rgba(0,0,0,0.25)',
  },
  brand: {
    padding: '24px 20px 20px',
    borderBottom: '1px solid rgba(200,151,58,0.25)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
  },
  logoWrap: {
    width: 56,
    height: 56,
    borderRadius: '50%',
    background: 'var(--ka-gold)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 26,
    color: 'var(--ka-navy)',
    fontWeight: 700,
    fontFamily: 'var(--font-display)',
    boxShadow: '0 2px 12px rgba(200,151,58,0.4)',
    letterSpacing: '-1px',
  },
  brandName: {
    fontFamily: 'var(--font-display)',
    fontSize: 15,
    fontWeight: 700,
    color: 'var(--ka-white)',
    textAlign: 'center',
    lineHeight: 1.3,
  },
  brandSub: {
    fontSize: 10,
    color: 'var(--ka-gold-light)',
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    textAlign: 'center',
    marginTop: -4,
  },
  // Mobile-only close button inside the drawer header
  closeBtn: {
    position: 'absolute',
    top: 12,
    right: 12,
    width: 34,
    height: 34,
    border: 'none',
    background: 'rgba(255,255,255,0.08)',
    color: 'var(--ka-white)',
    borderRadius: 8,
    fontSize: 18,
    lineHeight: 1,
    cursor: 'pointer',
  },
  userSection: {
    padding: '16px 20px',
    borderBottom: '1px solid rgba(255,255,255,0.07)',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    objectFit: 'cover',
    border: '2px solid var(--ka-gold)',
    flexShrink: 0,
  },
  avatarFallback: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    background: 'var(--ka-navy-mid)',
    border: '2px solid var(--ka-gold)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--ka-gold)',
    fontSize: 14,
    fontWeight: 700,
    flexShrink: 0,
  },
  userName: {
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--ka-white)',
    lineHeight: 1.3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  userRole: {
    fontSize: 11,
    color: 'var(--ka-gold-light)',
    letterSpacing: '0.04em',
  },
  nav: {
    flex: 1,
    padding: '12px 0',
  },
  navSection: {
    padding: '8px 16px 4px',
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: 'rgba(255,255,255,0.35)',
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 20px',
    fontSize: 14,
    color: 'rgba(255,255,255,0.75)',
    transition: 'all var(--transition)',
    borderLeftWidth: '3px',
    borderLeftStyle: 'solid',
    borderLeftColor: 'transparent',
    cursor: 'pointer',
  },
  navItemActive: {
    color: 'var(--ka-white)',
    background: 'rgba(200,151,58,0.15)',
    borderLeftColor: 'var(--ka-gold)',
  },
  navIcon: {
    fontSize: 16,
    width: 20,
    textAlign: 'center',
    flexShrink: 0,
  },
  navIconImg: {
    width: 20,
    height: 20,
    objectFit: 'contain',
    flexShrink: 0,
    display: 'block',
  },
  divider: {
    height: 1,
    background: 'rgba(255,255,255,0.07)',
    margin: '8px 0',
  },
  moduleStatus: {
    margin: '0 12px 12px',
    padding: '10px 12px',
    background: 'rgba(0,0,0,0.2)',
    borderRadius: 6,
    fontSize: 11,
  },
  moduleStatusTitle: {
    color: 'var(--ka-gold-light)',
    fontWeight: 700,
    marginBottom: 4,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    fontSize: 10,
  },
  moduleStatusItem: {
    color: 'rgba(255,255,255,0.6)',
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    marginTop: 2,
  },
  signOutBtn: {
    display: 'block',
    width: 'calc(100% - 24px)',
    margin: '0 12px 16px',
    padding: '10px',
    background: 'rgba(255,255,255,0.07)',
    color: 'rgba(255,255,255,0.6)',
    borderRadius: 6,
    fontSize: 13,
    textAlign: 'center',
    transition: 'all var(--transition)',
    border: '1px solid rgba(255,255,255,0.1)',
    cursor: 'pointer',
  },
};

// Small helper component so we don't repeat the branching logic
function ToolIcon({ tool }) {
  if (tool.iconType === 'image') {
    return (
      <img
        src={tool.iconDark || tool.icon}
        alt=""
        style={styles.navIconImg}
      />
    );
  }
  return <span style={styles.navIcon}>{tool.icon}</span>;
}

export default function Sidebar({ isMobile = false, open = false, onClose = () => {} }) {
  const { profile, logout, isSuperAdmin } = useAuth();
  const { getAccessibleTools } = useAccess();
  const navigate = useNavigate();
  const [hoveredSign, setHoveredSign] = useState(false);

  const tools = getAccessibleTools();

  const initials = profile?.displayName
    ? profile.displayName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
    : '?';

  const handleSignOut = async () => {
    await logout();
    navigate('/login');
  };

  const navItemStyle = (isActive) => ({
    ...styles.navItem,
    ...(isActive ? styles.navItemActive : {}),
  });

  // On mobile the sidebar becomes a slide-in drawer; on desktop it's the same
  // fixed rail as before.
  const sidebarStyle = {
    ...styles.sidebar,
    ...(isMobile
      ? {
          transform: open ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.25s ease',
          zIndex: 200,
        }
      : {}),
  };

  // Tapping a nav link should close the drawer on mobile.
  const handleNav = () => {
    if (isMobile) onClose();
  };

  return (
    <aside style={sidebarStyle}>

      {/* Brand */}
      <div style={styles.brand}>
        {isMobile && (
          <button style={styles.closeBtn} aria-label="Close menu" onClick={onClose}>
            ✕
          </button>
        )}
        <div style={styles.logoWrap}>KA</div>
        <div>
          <div style={styles.brandName}>King's Academy</div>
          <div style={styles.brandSub}>Faculty Tool Kit</div>
        </div>
      </div>

      {/* User info */}
      {profile && (
        <div style={styles.userSection}>
          {profile.photoURL ? (
            <img
              src={profile.photoURL}
              alt=""
              style={styles.avatar}
              onError={(e) => {
                e.target.onerror = null;
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div
            style={{
              ...styles.avatarFallback,
              display: profile.photoURL ? 'none' : 'flex',
            }}
          >
            {initials}
          </div>
          <div style={{ overflow: 'hidden' }}>
            <div style={styles.userName}>
              {profile.displayName || profile.email?.split('@')[0] || 'User'}
            </div>
            <div style={styles.userRole}>
              {isSuperAdmin ? '⚡ Super Admin' : (ROLE_LABELS[profile.role] || profile.role)}
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav style={styles.nav}>

        <div style={styles.navSection}>Main</div>
        <NavLink to="/" end onClick={handleNav}>
          {({ isActive }) => (
            <div style={navItemStyle(isActive)}>
              <span style={styles.navIcon}>🏠</span> Home
            </div>
          )}
        </NavLink>

        {tools.length > 0 && (
          <>
            <div style={styles.navSection}>Tools</div>
            {tools.map(tool => (
              <NavLink to={tool.path} key={tool.id} onClick={handleNav}>
                {({ isActive }) => (
                  <div style={navItemStyle(isActive)}>
                    <ToolIcon tool={tool} />
                    {tool.label}
                  </div>
                )}
              </NavLink>
            ))}
          </>
        )}

        {/* Super Admin section — only visible to you */}
        {isSuperAdmin && (
          <>
            <div style={styles.divider} />
            <div style={styles.navSection}>Super Admin</div>
            <NavLink to="/superadmin" onClick={handleNav}>
              {({ isActive }) => (
                <div style={navItemStyle(isActive)}>
                  <span style={styles.navIcon}>⚡</span> Super Admin
                </div>
              )}
            </NavLink>
          </>
        )}

      </nav>

      {/* Module status — Super Admin only */}
        {isSuperAdmin && tools.length > 0 && (
        <div style={styles.moduleStatus}>
          <div style={styles.moduleStatusTitle}>Module Status</div>
          {tools.map(tool => (
            <div key={tool.id} style={styles.moduleStatusItem}>
              <span style={{ color: '#4ade80', fontSize: 10 }}>✓</span>
              {tool.label}
            </div>
          ))}
        </div>
      )}

      {/* Sign out */}
      <button
        style={{
          ...styles.signOutBtn,
          ...(hoveredSign ? { background: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.85)' } : {}),
        }}
        onMouseEnter={() => setHoveredSign(true)}
        onMouseLeave={() => setHoveredSign(false)}
        onClick={handleSignOut}
      >
        Sign Out
      </button>

    </aside>
  );
}